<script setup lang="ts">

import { computed } from "vue";
import { DefaultSections, usePresentation } from "@presentation/Types";

const presentation = usePresentation();
const isAtEnd = computed( () => presentation.isAtEnd );
const hasAnimation = computed( () => presentation.triggers > 0 && presentation.currentTrigger < presentation.triggers - 1 );
const canProceed = computed( () => presentation.canProceed == null || presentation.canProceed() == true );
const hasAppendix = computed( () => presentation.sections.filter( s => s.isAppendix && s.isHidden !== true ).length > 0 )

</script>

<template>

    <div class="w-full max-w-full px-6 md:w-auto z-10 2xl:scale-110">

        <v-menu location="top">
            <template v-slot:activator="{ props, isActive }">
                <div 
                    class="
                        border border-white/5 rounded-lg py-1.5 pl-1 pr-4 w-full 
                        flex gap-2 justify-center items-center shadow-lg transition-all duration-75"
                    :style="{ 
                        background: `linear-gradient( to bottom, rgb( from ${presentation.theme.navColorFrom} r g b / 93%), rgb( from ${presentation.theme.navColorTo} r g b / 100%) )`,
                        border: presentation.theme.navBorderColor == null ? undefined : `solid 2px ${presentation.theme.navBorderColor}`
                    }">

                    <button v-bind="props"
                            class="h-12 w-12 rounded-lg cursor-pointer">
                        <div class="grid justify-items-center gap-1.5"
                            :style="{ color: presentation.theme.textColor }">
                            <span class="h-[3px] w-6 rounded-full transition" :class="isActive ? 'rotate-45 translate-y-[9px] bg-rose-500' : 'bg-current'"></span>
                            <span class="h-[3px] w-6 rounded-full bg-current transition" :class="isActive ? 'scale-x-0' : ''"></span>
                            <span class="h-[3px] w-6 rounded-full transition" :class="isActive ? '-rotate-45 -translate-y-[9px] bg-rose-500' : 'bg-current'"></span>
                        </div>
                    </button>
            
                    <h1 class="flex grow text-xs md:text-sm lg:text-base uppercase font-semibold pr-3">
                        {{ presentation.currentSection.title }}
                    </h1>
                    
                    <!-- GO NEXT -->
                    <button 
                        v-if="isAtEnd == false && ( presentation.currentSection.isAppendix == false || presentation.pageIndex < presentation.currentSection.pages.length - 1 || presentation.onSetNext != null )"
                        class="py-1 px-3 rounded tracking-wider uppercase shadow font-bold"
                        :disabled="canProceed == false"
                        :class="{
                            'opacity-30': canProceed == false
                        }"
                        :style="{
                            background: hasAnimation ? presentation.theme.triggerButtonBackground : presentation.theme.nextButtonBackground,
                            color: hasAnimation ? presentation.theme.triggerButtonText : presentation.theme.nextButtonText,
                        }" 
                        @click.stop="() => presentation.next()">
                        {{ hasAnimation ? 'Click' : 'Next' }}
                    </button>

                    <!-- RESET -->
                    <button 
                        v-else
                        class="py-1 px-3 rounded tracking-wider uppercase shadow font-bold"
                        :style="{
                            background: presentation.theme.nextButtonBackground,
                            color: presentation.theme.nextButtonText,
                        }"
                        @click.stop="() => presentation.reset()">
                        Restart
                    </button>
                    
                </div>
            </template>

            <v-list class="p-0 !rounded-md">
                <template 
                    v-for="item in presentation.sections.filter( s => s.isAppendix == false && s.isHidden !== true )"
                    :key="item.name">
                    <v-list-item 
                        :value="item.name"
                        density="compact"
                        class="bg-white !min-h-[36px]"
                        @click="() => presentation.goto( item.name )">
                        <v-list-item-title class="font-semibold">
                            {{ item.title }}
                        </v-list-item-title>
                    </v-list-item>
                </template>
                <div v-if="hasAppendix" class="h-px bg-gray-200" />
                <div v-if="hasAppendix" class="text-xs uppercase bg-gray-100 pt-1 px-4 text-cyan-700/50 -mb-1 z-10">Appendix</div>
                <template 
                    v-if="hasAppendix"
                    v-for="item in presentation.sections.filter( s => s.isAppendix && s.isHidden !== true )"
                    :key="item.name">
                    <v-list-item 
                        :value="item.name"
                        density="compact"
                        class="text-cyan-600 bg-gray-100 p-0 !min-h-[36px]"
                        @click="() => presentation.goto( item.name )">
                        <v-list-item-title class="font-semibold !py-0">
                            {{ item.title }}
                        </v-list-item-title>
                    </v-list-item>
                </template>
                <div class="h-px bg-gray-200" />
                <v-list-item 
                    density="compact"
                    class="bg-white !min-h-[36px]"
                    @click="() => presentation.goto( DefaultSections.SlideSorter )">
                    <v-list-item-title class="font-semibold flex items-center gap-3">
                        <Icon name="grid" />Slide Sorter
                    </v-list-item-title>
                </v-list-item>
            </v-list>

        </v-menu> 

    </div>

</template>
